import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import BgnIcon from "../img/logobgn250.png";
import { basecode } from "../mainscreen/pesananscreen/baserobotocode";
import { basecodeBold } from "../mainscreen/pesananscreen/baserobotoboldcode";
import { formatNumber } from "../numberingformat"; // Adjust the import

// Helper: safe integer
const toInt = (v) => (v == null ? 0 : Number(v) || 0);

// Helper: ID day-month (e.g., "10 September")
const fmtDayMonthID = (d) =>
  new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "long" });

// Helper: local YYYY-MM-DD (Jakarta)
const ymdID = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Asia/Jakarta",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const dateKeyLocal = (d) => (d ? ymdID.format(new Date(d)) : "0000-00-00");

// ===== MAIN =====
const downloadWeeklyPDF = async (data) => {
  const doc = new jsPDF();
  const kitchenName = localStorage.getItem("kitchenname") || "Dapur";

  // Fonts
  doc.addFileToVFS("Roboto-Regular.ttf", basecode);
  doc.addFileToVFS("Roboto-Bold.ttf", basecodeBold);
  doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
  doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");
  doc.setFont("Roboto", "normal");
  const LEFT_MARGIN = 30; // wider left margin (adjust as you like)
  const RIGHT_MARGIN = 20; // keep right margin narrow

  // Logo
  const loadImageAsBase64 = (imgSrc) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = imgSrc;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (err) => reject(err);
    });
  const iconBase64 = await loadImageAsBase64(BgnIcon);

  const pageWidth = doc.internal.pageSize.width;
  const tableWidth = pageWidth - (LEFT_MARGIN + RIGHT_MARGIN); // margin 14
  const columnStylesCommon = {
    0: { cellWidth: 10, halign: "center", fontSize: 6 },
    1: { cellWidth: tableWidth * 0.2, fontSize: 6 },
    2: { cellWidth: tableWidth * 0.1, halign: "right", fontSize: 6 },
    3: { cellWidth: tableWidth * 0.1, halign: "right", fontSize: 6 },
    4: { cellWidth: tableWidth * 0.1, halign: "right", fontSize: 6 },
    5: { cellWidth: tableWidth * 0.1, halign: "right", fontSize: 6 },
    6: { cellWidth: tableWidth * 0.1, halign: "right", fontSize: 6 },
    7: { cellWidth: tableWidth * 0.1, halign: "right", fontSize: 6 },
    8: { cellWidth: tableWidth * 0.16, fontSize: 6 },
  };

  // MEDIUM colors (readable, not too dark/light)
  const C_GOLD = [210, 160, 30];
  const C_RED = [180, 60, 60];
  const C_BROWN = [150, 100, 40];
  const C_PURPLE = [1, 24, 70];
  const C_PLUM = [140, 80, 110];
  const C_BANNER = [110, 80, 40];
  const C_MILDBLUE = [179, 224, 232];
  const C_DARKBLUE = [1, 24, 70];

  const tableStyles = {
    styles: {
      font: "Roboto",
      fontSize: 9,
      lineWidth: 0.2,
      lineColor: [0, 0, 0],
      cellPadding: { top: 1.5, right: 3, bottom: 1.5, left: 3 },
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineWidth: 0.2,
      lineColor: [0, 0, 0],
      fontSize: 6,
    },
  };

  // ===== Header (NOW takes periode + week number) =====
  const addHeader = (periodeStartText, periodeEndText, weekNo) => {
    const textYOffset = 10;
    const centerX = pageWidth / 2;

    // Logo left
    const iconWidth = 30;
    const iconHeight = 30;
    const iconX = LEFT_MARGIN;
    const iconY = textYOffset + 2;
    doc.addImage(iconBase64, "PNG", iconX, iconY, iconWidth, iconHeight);

    // Centered lines
    const lines = [
      "LAPORAN REALISASI PAKET MBG",
      `PERIODE ${periodeStartText} s.d. ${periodeEndText}`.toUpperCase(),
      `MINGGU KE-${weekNo}`,
    ];

    let addrY = textYOffset + iconHeight / 2 - 4;
    doc.setFont("Roboto", "bold");
    doc.setFontSize(10);
    lines.forEach((line) => {
      doc.text(line, pageWidth / 2, addrY, { align: "center" });
      addrY += 5;
    });

    // return Y to start tables
    return addrY + 10;
  };

  const namaSppg = "SPPG CISBINHAR";
  const kelurahan = "CISARANTEN BINA HARAPAN";
  const kecamatan = "ARCAMANIK";
  const kabkot = "KOTA BANDUNG";
  const provinsi = "JAWA BARAT";

  // Add an info table (no header row) with Nama, Kecamatan, Kelurahan
  const addInfoTable = (doc, startY, tableWidth) => {
    const margin = 14;

    autoTable(doc, {
      startY,
      theme: "grid",
      tableWidth,
      margin: { left: LEFT_MARGIN, right: RIGHT_MARGIN + 10 },
      body: [
        ["NAMA SPPG", `: ${data[0].kitchen_name}` || "-"],
        ["ALAMAT", `: ${data[0].kitchen_address}` || "-"],
        // ["KECAMATAN", `: ${kecamatan}` || "-"],
        // ["KABUPATEN/KOTA", `: ${kabkot}` || "-"],
        // ["PROVINSI", `: ${provinsi}` || "-"],
      ],
      styles: {
        font: "Roboto",
        fontSize: 6,
        cellPadding: 3,
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: tableWidth * 0.3, fontStyle: "bold" }, // label column
        1: { cellWidth: tableWidth * 0.72 }, // value column
      },
      head: [], // 👈 no header row
    });

    return doc.lastAutoTable.finalY + 8; // return new cursorY with spacing
  };

  // ===== Group by week_no =====
  const weeks = Object.values(
    data.reduce((acc, row) => {
      const w = row.week_no ?? "UNKNOWN";
      (acc[w] ||= { weekNo: w, rows: [] }).rows.push(row);
      return acc;
    }, {}),
  ).sort((a, b) => Number(a.weekNo) - Number(b.weekNo));

  // ===== Per week: header + tables grouped by DATE =====
  let isFirstWeek = true;
  let fileFirstStart = null;
  let fileLastEnd = null;

  for (const week of weeks) {
    // find periode start/end for this week
    const dates = week.rows
      .map((r) => new Date(r.date))
      .filter((d) => !isNaN(d))
      .sort((a, b) => a - b);

    const periodeStartText = dates.length ? fmtDayMonthID(dates[0]) : "-";
    const periodeEndText = dates.length
      ? fmtDayMonthID(dates[dates.length - 1])
      : "-";

    // keep for filename
    if (!fileFirstStart && dates.length) fileFirstStart = dates[0];
    if (dates.length) fileLastEnd = dates[dates.length - 1];

    if (!isFirstWeek) doc.addPage();
    isFirstWeek = false;

    let cursorY = addHeader(periodeStartText, periodeEndText, week.weekNo);
    cursorY = addInfoTable(doc, cursorY, tableWidth);

    // Group THIS WEEK by DATE
    const groupedByDate = week.rows.reduce((acc, row) => {
      const key = dateKeyLocal(row.date);
      (acc[key] ||= []).push(row);
      return acc;
    }, {});
    const dateKeys = Object.keys(groupedByDate).sort((a, b) =>
      a.localeCompare(b),
    );

    // Helpers for rendering
    const makeHeadRowGroup = (prettyDate, nameColTitle) => [
      [
        {
          content: "NO",
          rowSpan: 2,
          styles: {
            halign: "center",
            valign: "middle",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
        {
          content: nameColTitle,
          rowSpan: 2,
          styles: {
            halign: "center",
            valign: "middle",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
        {
          content: "KESELURUHAN",
          colSpan: 3,
          styles: {
            halign: "center",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
        {
          content: `REALISASI ${prettyDate}`,
          colSpan: 3,
          styles: {
            halign: "center",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
        {
          content: "KETERANGAN",
          rowSpan: 2,
          styles: {
            halign: "center",
            valign: "middle",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
      ],
      [
        {
          content: "Siswa Kelas 1-3",
          styles: {
            halign: "center",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
        {
          content: "Siswa Kelas 4-6",
          styles: {
            halign: "center",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
        {
          content: "Jumlah",
          styles: {
            halign: "center",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
        {
          content: "Siswa Kelas 1-3",
          styles: {
            halign: "center",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
        {
          content: "Siswa Kelas 4-6",
          styles: {
            halign: "center",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
        {
          content: "Jumlah",
          styles: {
            halign: "center",
            fillColor: C_MILDBLUE,
            textColor: [0, 0, 0],
          },
        },
      ],
    ];

    const renderTable = ({
      title,
      nameColTitle,
      rows,
      totals,
      startY,
      prettyDate,
    }) => {
      // Section banner
      const leftTitle =
        nameColTitle === "NAMA SEKOLAH"
          ? "JUMLAH SISWA SEKOLAH"
          : "JUMLAH POSYANDU";

      const rightTitle = nameColTitle === "NAMA SEKOLAH" ? prettyDate : "";

      autoTable(doc, {
        startY,
        theme: "grid",
        tableWidth,
        margin: { left: LEFT_MARGIN, right: RIGHT_MARGIN },
        head: [
          [
            {
              content: "", // right part
              colSpan: 2, // remaining columns
              styles: {
                halign: "right", // right aligned
                font: "Roboto",
                fontStyle: "bold",
                textColor: [255, 255, 255],
                fillColor: C_DARKBLUE,
              },
            },
            {
              content: leftTitle, // left part
              colSpan: 6, // span some columns
              styles: {
                halign: "center", // left/center as you like
                font: "Roboto",
                fontStyle: "bold",
                textColor: [255, 255, 255],
                fillColor: C_DARKBLUE,
              },
            },
            {
              content: rightTitle, // right part
              colSpan: 2, // remaining columns
              styles: {
                halign: "right", // right aligned
                font: "Roboto",
                fontStyle: "bold",
                textColor: [255, 255, 255],
                fillColor: C_DARKBLUE,
              },
            },
          ],
        ],
        body: [],
        ...tableStyles,
        columnStyles: columnStylesCommon,
      });

      const groupHead = makeHeadRowGroup(prettyDate, nameColTitle);

      autoTable(doc, {
        startY: doc.lastAutoTable?.finalY || startY,
        theme: "grid",
        tableWidth,
        margin: { left: LEFT_MARGIN, right: RIGHT_MARGIN },
        head: groupHead,
        body: [
          ...rows.map((r) => r.map(String)),
          [
            {
              content: "TOTAL",
              colSpan: 2,
              styles: { halign: "center", font: "Roboto", fontStyle: "normal" },
            },
            formatNumber(totals.kes13),
            formatNumber(totals.kes46),
            formatNumber(totals.kesJml),
            formatNumber(totals.real13),
            formatNumber(totals.real46),
            formatNumber(totals.realJml),
            "",
          ],
        ],
        ...tableStyles,
        columnStyles: columnStylesCommon,
        didParseCell: (d) => {
          const isLast = d.section === "body" && d.row.index === rows.length;
          if (isLast) {
            d.cell.styles.fontStyle = "bold";
            d.cell.styles.halign = d.column.index <= 1 ? "center" : "right";
            if (d.column.index >= 2 && d.column.index <= 4) {
              d.cell.styles.fillColor = C_MILDBLUE;
              d.cell.styles.textColor = [0, 0, 0];
            }
            if (d.column.index >= 5 && d.column.index <= 7) {
              d.cell.styles.fillColor = C_MILDBLUE;
              d.cell.styles.textColor = [0, 0, 0];
            }
          }
        },
      });

      return doc.lastAutoTable.finalY;
    };

    // Loop dates inside the week
    for (const key of dateKeys) {
      const items = groupedByDate[key];
      const prettyDate = new Date(key)
        .toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "long",
        })
        .toUpperCase();

      doc.setFont("Roboto", "bold");
      // SEKOLAH
      const sekolahItems = items.filter((x) => x.customer_type === "SEKOLAH");
      let sekolahTotals = {
        kes13: 0,
        kes46: 0,
        kesJml: 0,
        real13: 0,
        real46: 0,
        realJml: 0,
      };
      if (sekolahItems.length) {
        const rows = sekolahItems.map((it, i) => {
          const kes13 = toInt(it.kelas_13_sch);
          const kes46 = toInt(it.kelas_46_sch);
          const kesJml = toInt(it.jumlah_sch ?? kes13 + kes46);
          const real13 = toInt(it.kelas_13_real);
          const real46 = toInt(it.kelas_46_real);
          const realJml = toInt(it.jumlah_real ?? real13 + real46);
          const note = it.note;

          return [
            formatNumber(i + 1),
            it.customer_name || "-",
            formatNumber(kes13),
            formatNumber(kes46),
            formatNumber(kesJml),
            formatNumber(real13),
            formatNumber(real46),
            formatNumber(realJml),
            note !== null ? note : "",
          ];
        });
        sekolahTotals = {
          kes13: rows.reduce((s, r) => s + toInt(r[2]), 0),
          kes46: rows.reduce((s, r) => s + toInt(r[3]), 0),
          kesJml: rows.reduce((s, r) => s + toInt(r[4]), 0),
          real13: rows.reduce((s, r) => s + toInt(r[5]), 0),
          real46: rows.reduce((s, r) => s + toInt(r[6]), 0),
          realJml: rows.reduce((s, r) => s + toInt(r[7]), 0),
        };
        cursorY = renderTable({
          nameColTitle: "NAMA SEKOLAH",
          rows,
          totals: sekolahTotals,
          startY: cursorY,
          prettyDate,
        });
      }

      // POSYANDU
      const posItems = items.filter((x) => x.customer_type === "POSYANDU");
      let posTotals = {
        kes13: 0,
        kes46: 0,
        kesJml: 0,
        real13: 0,
        real46: 0,
        realJml: 0,
      };
      if (posItems.length) {
        const rows = posItems.map((it, i) => {
          const kes13 = toInt(it.kelas_13_sch);
          const kes46 = toInt(it.kelas_46_sch);
          const kesJml = toInt(it.jumlah_sch ?? kes13 + kes46);
          const real13 = toInt(it.kelas_13_real);
          const real46 = toInt(it.kelas_46_real);
          const realJml = toInt(it.jumlah_real ?? real13 + real46);
          const note = it.note;

          return [
            formatNumber(i + 1),
            it.customer_name || "-",
            formatNumber(kes13),
            formatNumber(kes46),
            formatNumber(kesJml),
            formatNumber(real13),
            formatNumber(real46),
            formatNumber(realJml),
            note !== null ? note : "",
          ];
        });
        posTotals = {
          kes13: rows.reduce((s, r) => s + toInt(r[2]), 0),
          kes46: rows.reduce((s, r) => s + toInt(r[3]), 0),
          kesJml: rows.reduce((s, r) => s + toInt(r[4]), 0),
          real13: rows.reduce((s, r) => s + toInt(r[5]), 0),
          real46: rows.reduce((s, r) => s + toInt(r[6]), 0),
          realJml: rows.reduce((s, r) => s + toInt(r[7]), 0),
        };
        cursorY = renderTable({
          nameColTitle: "NAMA POSYANDU",
          rows,
          totals: posTotals,
          startY: cursorY,
          prettyDate,
        });
      }

      // GRAND TOTAL (for that date)
      if (sekolahItems.length || posItems.length) {
        const grand = {
          kes13: (sekolahTotals.kes13 || 0) + (posTotals.kes13 || 0),
          kes46: (sekolahTotals.kes46 || 0) + (posTotals.kes46 || 0),
          kesJml: (sekolahTotals.kesJml || 0) + (posTotals.kesJml || 0),
          real13: (sekolahTotals.real13 || 0) + (posTotals.real13 || 0),
          real46: (sekolahTotals.real46 || 0) + (posTotals.real46 || 0),
          realJml: (sekolahTotals.realJml || 0) + (posTotals.realJml || 0),
        };

        autoTable(doc, {
          startY: cursorY,
          theme: "grid",
          tableWidth,
          margin: { left: LEFT_MARGIN, right: RIGHT_MARGIN },
          body: [
            [
              {
                content: "JUMLAH KESELURUHAN",
                colSpan: 2,
                styles: { halign: "center", fontStyle: "bold" },
              },
              formatNumber(grand.kes13),
              formatNumber(grand.kes46),
              formatNumber(grand.kesJml),
              formatNumber(grand.real13),
              formatNumber(grand.real46),
              formatNumber(grand.realJml),
              "",
            ],
          ],

          styles: tableStyles.styles,
          columnStyles: columnStylesCommon,
          didParseCell: (d) => {
            d.cell.styles.fontStyle = "bold";
            d.cell.styles.halign = d.column.index <= 1 ? "center" : "right";
            if (d.column.index >= 2 && d.column.index <= 4) {
              d.cell.styles.fillColor = C_MILDBLUE;
              d.cell.styles.textColor = [0, 0, 0];
            }
            if (d.column.index >= 5 && d.column.index <= 7) {
              d.cell.styles.fillColor = C_MILDBLUE;
              d.cell.styles.textColor = [0, 0, 0];
            }
          },
        });
      }

      if (dateKeys.indexOf(key) !== dateKeys.length - 1) {
        doc.addPage();
        cursorY = addHeader(periodeStartText, periodeEndText, week.weekNo);
        cursorY = addInfoTable(doc, cursorY, tableWidth);
      }
    }
  }

  // Filename: from first-week start to last-week end
  const rangeStart = fileFirstStart ? ymdID.format(fileFirstStart) : "start";
  const rangeEnd = fileLastEnd ? ymdID.format(fileLastEnd) : "end";

  // add footer with page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footerText = `Halaman ${i}/${pageCount}`;
    doc.setFontSize(8);
    doc.setFont("Roboto", "normal");
    doc.text(
      footerText,
      doc.internal.pageSize.width - doc.getTextWidth(footerText) - RIGHT_MARGIN,
      doc.internal.pageSize.height - 10,
    );
  }

  doc.save(`Laporan_Periodik_${rangeStart}_s.d._${rangeEnd}.pdf`);
};

export default downloadWeeklyPDF;
